/**
 * Patient Lifecycle Domain Helpers
 * 
 * Helper functions for working with v2 API lifecycle and capabilities enrichment.
 * These functions provide type-safe access to patient permissions with sensible defaults.
 * 
 * @feature 011-patient-lifecycle-permissions
 */

import { IPatientResponseDTO } from './patient';
import { PatientLifecycleState, CapabilityReasonCode } from './api-enums';

// Re-export enums for convenience
export { PatientLifecycleState, CapabilityReasonCode } from './api-enums';

/**
 * Types of issues that can be reported for correction on locked patients
 */
export enum CorrectionIssueType {
  NAME_INCORRECT = 'name-incorrect',
  DATE_OF_BIRTH_INCORRECT = 'dob-incorrect',
  DIAGNOSIS_INCORRECT = 'diagnosis-incorrect',
  MUTATION_INCORRECT = 'mutation-incorrect',
  OTHER = 'other'
}

/**
 * Request to correct information on a locked patient.
 * Submitted for review by rettX admins.
 */
export interface CorrectionRequestDTO {
  /** The type of information that needs correction */
  issue_type: CorrectionIssueType;
  
  /** Optional description of what is incorrect (required when issue_type is 'other') */
  description?: string;
}

/**
 * Response from correction request submission
 */
export interface CorrectionRequestResponse {
  /** Unique ID of the submitted correction request */
  request_id: string;
  
  /** The patient's rettxid */
  patient_rettxid: string;
  
  /** The type of issue reported */
  issue_type: CorrectionIssueType;
  
  /** The description provided */
  description?: string;
  
  /** Current status of the request */
  status: 'pending' | 'in-review' | 'resolved' | 'rejected';
  
  /** Timestamp when request was received */
  created_at: string;
  
  /** User ID who created the request */
  created_by: string;
}

// ========== v2 API Helper Functions ==========

/**
 * Gets the patient's lifecycle state from v2 API enrichment.
 * Returns DRAFT as default when enrichment is not available.
 */
export function getLifecycleState(patient: IPatientResponseDTO | null | undefined): PatientLifecycleState {
  return patient?.lifecycle?.state ?? PatientLifecycleState.DRAFT;
}

/**
 * Checks if the patient can be edited based on v2 API capabilities.
 */
export function canEdit(patient: IPatientResponseDTO | null | undefined): boolean {
  return patient?.capabilities?.edit?.allowed ?? false;
}

/**
 * Checks if the patient can be deleted based on v2 API capabilities.
 */
export function canDelete(patient: IPatientResponseDTO | null | undefined): boolean {
  return patient?.capabilities?.delete?.allowed ?? false;
}

/**
 * Checks if correction request can be submitted based on v2 API capabilities.
 */
export function canRequestCorrection(patient: IPatientResponseDTO | null | undefined): boolean {
  return patient?.capabilities?.request_correction?.allowed ?? false;
}

/**
 * Checks if diagnosis files can be uploaded based on v2 API capabilities.
 * Returns false when enrichment is not available (safe default).
 * 
 * @param patient - The patient response DTO
 * @returns Whether upload is allowed
 */
export function canUploadDiagnosis(patient: IPatientResponseDTO | null | undefined): boolean {
  return patient?.capabilities?.upload_diagnosis?.allowed ?? false;
}

/**
 * Gets the translation key for why editing is disabled.
 * Returns empty string if edit is allowed.
 * 
 * @param patient - The patient response DTO
 * @returns Translation key for the disabled reason, or empty string
 */
export function getEditDisabledReason(patient: IPatientResponseDTO | null | undefined): string {
  if (canEdit(patient)) {
    return '';
  }
  const state = getLifecycleState(patient);
  // Use lifecycle state for the reason - it's more accurate than backend reason_code
  return `PATIENT.LIFECYCLE.EDIT_DISABLED.${state}`;
}

/**
 * Gets the translation key for why deletion is disabled.
 * Returns empty string if delete is allowed.
 * 
 * @param patient - The patient response DTO
 * @returns Translation key for the disabled reason, or empty string
 */
export function getDeleteDisabledReason(patient: IPatientResponseDTO | null | undefined): string {
  if (canDelete(patient)) {
    return '';
  }
  const state = getLifecycleState(patient);
  // Use lifecycle state for the reason - it's more accurate than backend reason_code
  return `PATIENT.LIFECYCLE.DELETE_DISABLED.${state}`;
}

/**
 * Converts lifecycle state to translation key format.
 * Example: PatientLifecycleState.UNDER_REVIEW → 'UNDER_REVIEW'
 * 
 * @param state - The lifecycle state
 * @returns Key for translation lookup
 */
export function getLifecycleStateKey(state: PatientLifecycleState): string {
  return state;
}

/**
 * Gets the icon name for a lifecycle state (Material Icons)
 * 
 * @param state - The lifecycle state
 * @returns Material icon name
 */
export function getLifecycleStateIcon(state: PatientLifecycleState): string {
  switch (state) {
    case PatientLifecycleState.DRAFT:
      return 'edit_note';
    case PatientLifecycleState.UNDER_REVIEW:
      return 'hourglass_top';
    case PatientLifecycleState.MORE_INFO_REQUIRED:
      return 'info';
    case PatientLifecycleState.CONFIRMED:
      return 'verified';
    case PatientLifecycleState.LOCKED:
      return 'lock';
    default:
      return 'help';
  }
}

/**
 * Determines the styling variant for a lifecycle state.
 * Used for visual differentiation in the UI.
 * 
 * @param state - The lifecycle state
 * @returns 'success' | 'warning' | 'info' | 'default'
 */
export function getLifecycleStateVariant(state: PatientLifecycleState): 'success' | 'warning' | 'info' | 'default' {
  switch (state) {
    case PatientLifecycleState.CONFIRMED:
    case PatientLifecycleState.LOCKED:
      return 'success';
    case PatientLifecycleState.MORE_INFO_REQUIRED:
      return 'warning';
    case PatientLifecycleState.UNDER_REVIEW:
      return 'info';
    case PatientLifecycleState.DRAFT:
    default:
      return 'default';
  }
}
